// src/app/settings/application/services/clock-control.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {
  ClockDevice,
  ClockDeviceApiResponse,
  ToggleClockDeviceActivePayload,
  UpdateClockDeviceNamePayload,
} from '../models/clock-control.model';

@Injectable({ providedIn: 'root' })
export class ClockControlService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all clock devices
   */
  getClockDevices(): Observable<ClockDevice[] | null> {
    return this.http.get<ClockDeviceApiResponse>(`${this.baseUrl}/api/clock-devices`).pipe(
      map((res) =>
        (res.data ?? []).map(
          (device): ClockDevice => ({
            id: device.id,
            name: device.name,
            deviceNumber: device.deviceNumber,
            ipAddress: device.ipAddress ?? '',
            latitude: Number(device.latitude ?? 0),
            longitude: Number(device.longitude ?? 0),
            isActive: !!device.isActive,
            lastSync: device.lastSync ?? null,
          }),
        ),
      ),
      catchError((err) => {
        console.error('Error fetching clock devices:', err);
        return of(null);
      }),
    );
  }

  /**
   * Update clock device name
   */
  updateClockDeviceName(id: number, payload: UpdateClockDeviceNamePayload): Observable<boolean> {
    return this.http.patch<{ status: string }>(`${this.baseUrl}/api/clock-devices/${id}`, payload).pipe(
      map(() => true),
      catchError((err) => {
        console.error(`Error updating clock device name for id ${id}:`, err);
        return of(false);
      }),
    );
  }

  /**
   * Toggle clock device active status
   */
  toggleClockDeviceActive(
    id: number,
    payload: ToggleClockDeviceActivePayload,
  ): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/clock-devices/${id}/toggle-active`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error(`Error toggling active state for clock device id ${id}:`, err);
          return of(false);
        }),
      );
  }
}